import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { checkPincode } from '../services/PincodeService';

const LocationContext = createContext(null);
const STORAGE_KEY = 'delivery_location';

const MESSAGES = {
  invalid: 'Please enter a valid pincode.',
  network: 'Unable to verify this pincode. Please try again.',
  notDeliverable: 'We currently do not deliver to this pincode.',
};

export const LocationProvider = ({ children }) => {
  const [pincode, setPincode] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || ''; } catch { return ''; }
  });
  const [locationInfo, setLocationInfo] = useState(null);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('idle');

  const persist = (val) => {
    try { localStorage.setItem(STORAGE_KEY, val); } catch { /* ignore */ }
  };

  const clearLocation = () => {
    setPincode('');
    setLocationInfo(null);
    setError('');
    setStatus('idle');
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  const applyPincode = useCallback(async (val) => {
    const pin = (val || '').trim().replace(/\D/g, '');
    if (!pin) {
      setStatus('error');
      setError(MESSAGES.invalid);
      return { ok: false, deliverable: false, message: MESSAGES.invalid };
    }
    setChecking(true);
    setStatus('checking');
    setError('');
    try {
      const info = await checkPincode(pin);
      setPincode(pin);
      setLocationInfo(info);
      if (info && info.deliverable) {
        setStatus('deliverable');
        persist(pin);
        return { ok: true, deliverable: true, message: null };
      }
      const msg = info?.message || MESSAGES.notDeliverable;
      setStatus('not-deliverable');
      setError(msg);
      return { ok: true, deliverable: false, message: msg };
    } catch (e) {
      setStatus('error');
      setError(MESSAGES.network);
      setPincode(pin);
      return { ok: false, deliverable: false, message: MESSAGES.network };
    } finally {
      setChecking(false);
    }
  }, []);

  // Restore previously saved location on mount
  useEffect(() => {
    if (pincode) {
      checkPincode(pincode).then(info => {
        setLocationInfo(info);
        if (info && info.deliverable) setStatus('deliverable');
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isProductDeliverable = useCallback((product) => {
    // No location selected => assume deliverable everywhere
    if (!pincode || !locationInfo) return true;
    const info = locationInfo;
    if (info.deliverable === false) return false;
    if (!product) return true;
    // Vendor-scoped product: only deliverable if that vendor serves the pincode
    if (product.vendorId) {
      if (info.vendors && info.vendors.length > 0) {
        return info.vendors.some(v => Number(v.id) === Number(product.vendorId));
      }
      return false;
    }
    // Global product: deliverable at any serviceable pincode
    return true;
  }, [pincode, locationInfo]);

  const deliveredCity = useMemo(() => {
    if (locationInfo && locationInfo.deliverable) {
      return [locationInfo.city, locationInfo.state].filter(Boolean).join(', ');
    }
    return '';
  }, [locationInfo]);

  const value = {
    pincode,
    locationInfo,
    deliveredCity,
    checking,
    error,
    status,
    applyPincode,
    clearLocation,
    isProductDeliverable,
  };

  return <LocationContext.Provider value={value}>{children}</LocationContext.Provider>;
};

export const useLocation = () => useContext(LocationContext);